<template>
  <div class="soar-container flex flex-col gap-y-4">
    <h1 class="text-xl md:text-2xl text-primary font-medium border-b border-brand-gray-300">
      {{ $t('Profile') }}
    </h1>

    <UserProfileSponsorSection v-if="showSponsorDataSection" />

    <Form
      v-if="!isLoading"
      :initial-values="userProfile"
      :keep-values="true"
      class="flex flex-wrap gap-y-2"
      v-slot="{ resetForm }"
      @submit="(values) => onSubmit(values, resetForm)"
    >
      <Field :rules="requiredRule" name="englishFirstName" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('EnglishFirstName')"
          label-class="w-full"
          class="px-3 w-full md:w-1/4"
          direction="flex-col"
        />
      </Field>
      <Field name="englishSecondName" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('EnglishSecondName')"
          :is-required="false"
          label-class="w-full"
          class="px-3 w-full md:w-1/4"
          direction="flex-col"
        />
      </Field>
      <Field name="englishThirdName" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('EnglishThirdName')"
          :is-required="false"
          label-class="w-full"
          class="px-3 w-full md:w-1/4"
          direction="flex-col"
        />
      </Field>
      <Field :rules="requiredRule" name="englishFamilyName" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('EnglishFamilyName')"
          label-class="w-full"
          class="px-3 w-full md:w-1/4"
          direction="flex-col"
        />
      </Field>
      <Field :rules="requiredRule" name="arabicFirstName" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('ArabicFirstName')"
          label-class="w-full"
          class="px-3 w-full md:w-1/4"
          direction="flex-col"
        />
      </Field>
      <Field name="arabicSecondName" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('ArabicSecondName')"
          :is-required="false"
          label-class="w-full"
          class="px-3 w-full md:w-1/4"
          direction="flex-col"
        />
      </Field>
      <Field name="arabicThirdName" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('ArabicThirdName')"
          :is-required="false"
          label-class="w-full"
          class="px-3 w-full md:w-1/4"
          direction="flex-col"
        />
      </Field>
      <Field :rules="requiredRule" name="arabicFamilyName" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('ArabicFamilyName')"
          label-class="w-full"
          class="px-3 w-full md:w-1/4"
          direction="flex-col"
        />
      </Field>
      <Field :rules="requiredRule" name="identityNumber" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('IdentityNumber')"
          is-disabled
          input-class="px-3 py-1.5"
          label-class="w-full"
          class="px-3 w-full md:w-1/2"
          direction="flex-col"
        />
      </Field>
      <Field
        :rules="requiredRule"
        name="dateOfBirth"
        v-slot="{ field, errors: errs, handleChange }"
      >
        <Datepicker
          :field="field"
          :errors="errs"
          :label="$t('DateOfBirth')"
          :default-value="field.value"
          input-class="ps-3 py-0.5"
          class="px-3 w-full md:w-1/2"
          max-date="__now__"
          @on-change="(date) => handleChange(date)"
        >
        </Datepicker>
      </Field>
      <Field :rules="requiredRule" name="genderId" v-slot="{ field, errors: errs, handleChange }">
        <Dropdown
          :field="field"
          :errors="errs"
          :label="$t('Gender')"
          :options="genders"
          class="px-3 w-full md:w-1/2"
          direction="flex-col"
          @on-selection="(item) => handleChange(+item?.code)"
        ></Dropdown>
      </Field>
      <Field name="religionId" v-slot="{ field, errors: errs, handleChange }">
        <Dropdown
          :field="field"
          :errors="errs"
          :label="$t('Religion')"
          :options="religions"
          :is-required="false"
          class="px-3 w-full md:w-1/2"
          direction="flex-col"
          @on-selection="(item) => handleChange(+item?.code)"
        ></Dropdown>
      </Field>
      <Field name="maritalStatus" v-slot="{ field, errors: errs, handleChange }">
        <Dropdown
          :field="field"
          :errors="errs"
          :label="$t('MaritalStatus')"
          :options="maritalStatuses"
          :is-required="false"
          class="px-3 w-full md:w-1/2"
          direction="flex-col"
          @on-selection="(item) => handleChange(+item?.code)"
        ></Dropdown>
      </Field>
      <Field
        :rules="requiredRule"
        name="currentNationalityID"
        v-slot="{ field, errors: errs, handleChange }"
      >
        <Dropdown
          :field="field"
          :errors="errs"
          :label="$t('Nationality')"
          :options="countries"
          input-class="px-3 py-1.5"
          class="px-3 w-full md:w-1/2"
          direction="flex-col"
          @on-selection="(item) => handleChange(+item?.code)"
        ></Dropdown>
      </Field>
      <Field name="passportNo" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('PassportNo')"
          :is-required="false"
          input-class="px-3 py-1.5"
          label-class="w-full"
          class="px-3 w-full md:w-1/2"
          direction="flex-col"
        />
      </Field>
      <div class="flex gap-4 flex-col md:flex-row"></div>
      <Field
        :rules="requiredRule"
        name="residenceCountryId"
        v-slot="{ field, errors: errs, handleChange }"
      >
        <Dropdown
          :field="field"
          :errors="errs"
          :label="$t('ResidenceCountry')"
          :options="countries"
          input-class="px-3 py-1.5"
          class="px-3 w-full md:w-1/2"
          direction="flex-col"
          @on-selection="(item) => handleChange(+item?.code)"
        ></Dropdown>
      </Field>
      <Field :rules="requiredRule" name="email" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('Email')"
          is-disabled
          input-class="px-3 py-1.5"
          label-class="w-full"
          class="px-3 w-full md:w-1/2"
          direction="flex-col"
        />
      </Field>
      <Field
        :rules="requiredRule"
        name="mobileNumber"
        v-slot="{ field, errors: errs, handleChange }"
      >
        <ComboText
          :field="field"
          :errors="errs"
          :label="$t('MobileNo')"
          :options="countries"
          input-class="px-3 py-1.5"
          class="px-3 w-full md:w-1/2"
          direction="flex-col"
          @on-selection="(phone) => handleChange(phone)"
        ></ComboText>
      </Field>
      <Field name="firstAddressDescription" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('FirstAddressDescription')"
          :is-required="false"
          input-class="px-3 py-1.5"
          label-class="w-full"
          class="px-3 w-full md:w-1/2"
          direction="flex-col"
        />
      </Field>
      <Field name="secondAddressDescription" v-slot="{ field, errors: errs }">
        <TextInput
          :field="field"
          :errors="errs"
          :label="$t('SecondAddressDescription')"
          :is-required="false"
          input-class="px-3 py-1.5"
          label-class="w-full"
          class="px-3 w-full md:w-1/2"
          direction="flex-col"
        />
      </Field>
      <Field
        :rules="requiredRule"
        name="preferedLanguageId"
        v-slot="{ field, errors: errs, handleChange }"
      >
        <Dropdown
          :field="field"
          :errors="errs"
          :label="$t('PreferedLanguage')"
          :options="languages"
          class="px-3 w-full md:w-1/2"
          direction="flex-col"
          @on-selection="(item) => handleChange(+item?.code)"
        ></Dropdown>
      </Field>
      <Field name="preferedTimeZone" v-slot="{ field, errors: errs, handleChange }">
        <Dropdown
          :field="field"
          :errors="errs"
          :label="$t('PreferedTimeZone')"
          :options="timeZones"
          :is-required="false"
          class="px-3 w-full md:w-1/2"
          direction="flex-col"
          @on-selection="(item) => handleChange(+item?.code)"
        ></Dropdown>
      </Field>
      <div class="flex gap-4 flex-col md:flex-row"></div>

      <div class="p-3 w-full flex justify-between">
        <RouterLink
          :to="{ name: 'AuthUserNotificationSettings' }"
          class="rounded-md bg-brand-green/80 pe-2 hover:bg-brand-green flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
        >
          <div class="p-2 bg-brand-green rounded-s-md">
            <faBell class="w-4 fill-white"></faBell>
          </div>
          {{ $t('accountNotificationsSettings') }}
        </RouterLink>
        <button
          class="min-w-28 text-center rounded-md bg-primary/80 pe-2 hover:bg-primary flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
        >
          <div class="p-2 bg-primary rounded-s-md">
            <faDiskette class="w-4 fill-white"></faDiskette>
          </div>
          <span class="px-3 w-full text-center">
            {{ $t('Save') }}
          </span>
        </button>
      </div>
    </Form>
  </div>
</template>

<script setup>
import faBell from '@/assets/icons/faBell.svg?component'
import faDiskette from '@/assets/icons/faDiskette.svg?component'
import client_lookups from '@/assets/json/clientLookups.json'
import ComboText from '@/components/SimpleForm/ComboText.vue'
import Datepicker from '@/components/SimpleForm/Datepicker.vue'
import Dropdown from '@/components/SimpleForm/Dropdown.vue'
import TextInput from '@/components/SimpleForm/TextInput.vue'
import axiosInstance from '@/services'
import { useAuthStore } from '@/stores/auth'
import { useLookupsStore } from '@/stores/lookups'
import UserProfileSponsorSection from '@/views/auth-user/user-profile/UserProfileSponsorSection.vue'
import { DateTime } from 'luxon'
import { Field, Form } from 'vee-validate'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { notify } from 'notiwind'
import { useRouter } from 'vue-router'

const router = useRouter()
const i18n = useI18n()

//#region rules
function requiredRule(value) {
  if (!value || !`${value}`.trim()) {
    return i18n.t('INVALID_REQUIRED')
  }

  return true
}

const authStore = useAuthStore()
const lookupsStore = useLookupsStore()

const userProfile = ref()
const isLoading = ref(true)

const genders = ref()
const religions = ref()
const maritalStatuses = ref()
const countries = ref()
const languages = ref()
const timeZones = ref()

// const {
//   application: { establishment, TypingCenter }
// } = client_lookups

// const applicationId = computed(() => authStore.user.applicationId)

const showSponsorDataSection = computed(() => false)
//   [establishment, TypingCenter].includes(applicationId.value)

onMounted(async () => {
  await nextTick()
  userProfile.value = await axiosInstance.get('/api/userProfile/current')
  userProfile.value.dateOfBirth = DateTime.fromISO(userProfile.value.dateOfBirth).toFormat(
    'dd/MM/yyyy'
  )

  genders.value = await lookupsStore.fetchLookup('/api/lookup/genders')
  religions.value = await lookupsStore.fetchLookup('/api/lookup/religions')
  maritalStatuses.value = await lookupsStore.fetchLookup('/api/lookup/maritalStatuses')
  countries.value = await lookupsStore.fetchLookup('/api/lookup/countries')
  languages.value = await lookupsStore.fetchLookup('/api/lookup/GetUsedLanguages')
  timeZones.value = await lookupsStore.fetchLookup('/api/lookup/timeZones')

  isLoading.value = false
})

const onSubmit = async (form) => {
  form.dateOfBirth = DateTime.fromFormat(form.dateOfBirth, 'dd/MM/yyyy').toFormat('MM/dd/yyyy')
  await axiosInstance.post('/api/userProfile/update', form)

  notify(
    {
      group: 'error',
      title: i18n.t('updatedSuccessfully')
    },
    6000
  )

  router.push('/')
}
</script>

<script>
export default {
  name: 'UserProfile'
}
</script>
<style lang="scss" scoped></style>
